import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { TipoTributarioService } from '@shared/services/apis/motor-fiscal/tipo-tributario.service';
import { requiredTrim } from '@shared/validators/string-trim.validator';

@Component({
  selector: 'app-tipo-tributario-cadastro',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSlideToggleModule,
    HeaderComponent, FooterActionComponent, LoadingBoxComponent,
  ],
  templateUrl: './tipo-tributario-cadastro.component.html',
  styleUrl: './tipo-tributario-cadastro.component.css',
})
export class TipoTributarioCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;
  public isLoading = false;
  public isSaving = false;
  public idTipoTributario: number | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private tipoTributarioService: TipoTributarioService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      codigo: ['', [Validators.required, requiredTrim, Validators.maxLength(10)]],
      descricao: ['', [Validators.required, requiredTrim, Validators.maxLength(120)]],
      isAtivo: [true],
    });

    const id = this.route.snapshot.paramMap.get('idTipoTributario');
    if (id) {
      this.idTipoTributario = Number(id);
      this.carregar(this.idTipoTributario);
    }
  }

  public get isEdicao(): boolean {
    return this.idTipoTributario != null;
  }

  public get titulo(): string {
    return this.isEdicao ? 'Editar Tipo Tributário' : 'Novo Tipo Tributário';
  }

  public get statusLabel(): string {
    return this.model?.get('isAtivo')?.value ? 'Ativo' : 'Inativo';
  }

  private carregar(id: number): void {
    this.isLoading = true;
    this.tipoTributarioService.getById(id).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar o tipo tributário', AlertType.error);
          this.cancelar();
          return;
        }
        this.model.patchValue({
          codigo: response.tipoTributario.codigo,
          descricao: response.tipoTributario.descricao,
          isAtivo: response.tipoTributario.isAtivo,
        });
      },
      error: error => {
        this.isLoading = false;
        this.alertService.show('Erro inesperado', error.message || 'Erro ao carregar o tipo tributário', AlertType.error);
        this.cancelar();
      },
    });
  }

  public salvar(): void {
    if (this.model.invalid) {
      this.model.markAllAsTouched();
      return;
    }

    const codigo = (this.model.get('codigo')?.value ?? '').trim();
    this.isSaving = true;

    this.existeCodigoDuplicado(codigo).subscribe(duplicado => {
      if (duplicado) {
        this.isSaving = false;
        this.alertService.show('Atenção', `Já existe um tipo tributário com o código "${codigo}".`, AlertType.warning);
        return;
      }
      this.persistir(codigo);
    });
  }

  /** RN02 — unicidade do código, sem diferenciar maiúsculas/minúsculas. */
  private existeCodigoDuplicado(codigo: string): Observable<boolean> {
    return this.tipoTributarioService.getAll({ pesquisa: codigo, paginaAtual: 1, tamanhoPagina: 50 }).pipe(
      map(response => {
        if (response.isError) return false;
        return response.itens.some(t =>
          t.codigo.toLowerCase() === codigo.toLowerCase() && t.idTipoTributario !== this.idTipoTributario
        );
      }),
      // Falha na verificação prévia não bloqueia o salvamento: a API valida a unicidade.
      catchError(() => of(false))
    );
  }

  private persistir(codigo: string): void {
    const request = {
      codigo,
      descricao: (this.model.get('descricao')?.value ?? '').trim(),
      isAtivo: !!this.model.get('isAtivo')?.value,
    };

    const request$ = this.isEdicao
      ? this.tipoTributarioService.alterar(this.idTipoTributario!, request)
      : this.tipoTributarioService.criar(request);

    request$.subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription || 'Erro ao salvar o tipo tributário', AlertType.warning);
          return;
        }
        this.alertService.show(
          'Sucesso',
          this.isEdicao ? 'Tipo tributário atualizado.' : 'Tipo tributário adicionado.',
          AlertType.success
        );
        this.voltarParaListagem();
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Erro inesperado', error.message || 'Erro ao salvar o tipo tributário', AlertType.error);
      },
    });
  }

  public cancelar(): void {
    this.voltarParaListagem();
  }

  private voltarParaListagem(): void {
    this.router.navigate(['/configuracoes/cadastros/tipos-tributarios']);
  }
}
